// src/pages/LandingPage.js
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, Shield } from "lucide-react";
import "./LandingPage.css";
import "../../App.css";

import LandingNavbar from "./LandingNavbar";

export default function LandingPage() {

  const { t } = useTranslation();

  // ✅ Make this page a true one-pager: no scrolling while mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);



  // ----- HERO SLIDES (translated) -----
  const slides = useMemo(
    () => [
      {
        key: "muscle",
        titleKey: "landing.slides.muscle.title",
        textKey: "landing.slides.muscle.text",
        img: "/images/olympeak_visual_1.png",
        tone: "blue",
      },
      {
        key: "weight",
        titleKey: "landing.slides.weight.title",
        textKey: "landing.slides.weight.text",
        img: "/images/olympeak_visual_2.png",
        tone: "pink",
      },
      {
        key: "stamina",
        titleKey: "landing.slides.stamina.title",
        textKey: "landing.slides.stamina.text",
        img: "/images/olympeak_visual_3.png",
        tone: "green",
      },
    ],
    []
  );

  const [current, setCurrent] = useState(0);

  // Auto-rotate
  useEffect(() => {
    const tmr = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 6000);
    return () => clearInterval(tmr);
  }, [slides.length]);

  return (
    <div className="lp">
      <LandingNavbar />

      {/* ===== FULL-VIEWPORT HERO ===== */}
      <section className="lp-hero">
        {slides.map((s, i) => {
          const isActive = i === current;
          if (!isActive) return null;

          return (
            <div key={s.key} className="lp-hero-slide">
              <motion.div
                className="lp-hero-bg-wrap"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              >
                <img src={s.img} alt="" className="lp-hero-bg" />
                <div className={`lp-hero-overlay tone-${s.tone}`} />
              </motion.div>

              <div className="lp-hero-content">
                <motion.div
                  className="lp-hero-text-inner"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  <h1 className="lp-hero-h1">
                    <Trans
                      i18nKey={s.titleKey}
                      components={{
                        br: <br />,
                        gradient: <span className="lp-gradient" />,
                        pink: <span className="lp-pink" />,
                        green: <span className="lp-green" />,
                      }}
                    />
                  </h1>

                  <p className="lp-hero-lead">{t(s.textKey)}</p>

                  <div className="lp-hero-actions">
                    <a href="/auth?mode=register">
                      <button className="btn primary">
                        {t("landing.cta.startFree")} <ArrowRight size={18} />
                      </button>
                    </a>

                    <div className="btn ghost">
                      <Shield size={16} /> {t("landing.badge.freeNoCard")}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          );
        })}

        <div className="lp-hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`lp-dot ${i === current ? "active" : ""}`}
              onClick={() => setCurrent(i)}
              aria-label={t("landing.dots.goToSlide", { n: i + 1 })}
            />
          ))}
        </div>

        {/* Footer overlay (no extra page height, no scroll) */}
        <footer className="lp-footer">
          <div className="lp-footer-inner">
            <p>
              © {new Date().getFullYear()} Olympeak. {t("landing.footer.rights")}
            </p>

            <div className="lp-footer-links">
              <a href="/auth?mode=login">{t("landing.footer.login")}</a>
              <a href="/terms" target="_blank" rel="noreferrer">
                {t("landing.footer.terms")}
              </a>
              <a href="/privacy" target="_blank" rel="noreferrer">
                {t("landing.footer.privacy")}
              </a>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}